import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environment';
import { normalizeApiArray } from '../utils/api-response.util';

export interface OnboardingOperator {
  operatorUserId?: number;
  operatorUserID?: number;
  userID?: number;
  id?: number;
  userName?: string;
  username?: string;
  email?: string;
  organisationID?: number | null;
  organisationId?: number | null;
  organisationName?: string | null;
  orgName?: string | null;
  statusID?: number | null;
  statusId?: number | null;
  statusName?: string | null;
}

export interface OnboardingSummary {
  operator?: any;
  vehicles?: any[];
  availableDeviceSIMs?: any[];
  devices?: any[];
  isReadyForFleetMonitor?: boolean;
  outstandingSteps?: string[];
}

export interface RouterOsScriptResult {
  deviceID?: number;
  vehicleID?: number;
  deviceSIMID?: number;
  primarySIMID?: number;
  secondarySIMID?: number;
  vehicleRegistration?: string;
  deviceSerialNumber?: string;
  routerOsScript?: string;
  publicDeparturesUrl?: string;
}

export interface OnboardingCompletionResponse {
  summary?: OnboardingSummary;
  routerOsScripts?: RouterOsScriptResult[];
}

export interface OnboardingDeviceAssignmentRequest {
  clientReference: string;
  vehicleID?: number;
  vehicleClientReference?: string;
  primarySIMID?: number;
  primarySIMClientReference?: string;
  secondarySIMID?: number;
  secondarySIMClientReference?: string;
  deviceModel: string;
  deviceType: string;
  deviceSerialNumber: string;
  deviceImei: string;
  generateRouterOsScript: boolean;
}

export interface OnboardingCompletionRequest {
  activateOperator: boolean;
  activateOrganisation: boolean;
  organisation?: Record<string, string | number>;
  vehicles?: any[];
  deviceSIMs?: any[];
  deviceAssignments: OnboardingDeviceAssignmentRequest[];
}

@Injectable({
  providedIn: 'root'
})
export class OperatorOnboardingService {
  private apiUrl = `${environment.apiUrl}/Onboarding`;

  constructor(private http: HttpClient) {}

  getOperators(filter?: { statusId?: number; search?: string }): Observable<OnboardingOperator[]> {
    let params = new HttpParams();

    if (filter?.statusId) {
      params = params.set('statusId', filter.statusId);
    }

    if (filter?.search?.trim()) {
      params = params.set('search', filter.search.trim());
    }

    return this.http.get<unknown>(`${this.apiUrl}/operators`, { params }).pipe(
      map(response => normalizeApiArray<OnboardingOperator>(response))
    );
  }

  getSummary(operatorUserId: number): Observable<OnboardingSummary> {
    return this.http.get<OnboardingSummary>(`${this.apiUrl}/operators/${operatorUserId}/summary`).pipe(
      map(summary => ({
        ...summary,
        vehicles: normalizeApiArray(summary?.vehicles || []),
        availableDeviceSIMs: normalizeApiArray(summary?.availableDeviceSIMs || []),
        devices: normalizeApiArray(summary?.devices || []),
        outstandingSteps: normalizeApiArray(summary?.outstandingSteps || [])
      }))
    );
  }

  complete(operatorUserId: number, payload: OnboardingCompletionRequest): Observable<OnboardingCompletionResponse> {
    return this.http.post<OnboardingCompletionResponse>(`${this.apiUrl}/operators/${operatorUserId}/complete`, payload).pipe(
      map(response => ({
        ...response,
        routerOsScripts: normalizeApiArray<RouterOsScriptResult>(response?.routerOsScripts || []),
        summary: response?.summary
          ? {
              ...response.summary,
              vehicles: normalizeApiArray(response.summary.vehicles || []),
              availableDeviceSIMs: normalizeApiArray(response.summary.availableDeviceSIMs || []),
              devices: normalizeApiArray(response.summary.devices || []),
              outstandingSteps: normalizeApiArray(response.summary.outstandingSteps || [])
            }
          : undefined
      }))
    );
  }

  regenerateRouterOsScript(deviceId: number): Observable<RouterOsScriptResult> {
    return this.http.get(`${this.apiUrl}/devices/${deviceId}/routeros-script`, { responseType: 'text' }).pipe(
      map(response => this.normalizeRouterOsScriptResponse(response, deviceId))
    );
  }

  private normalizeRouterOsScriptResponse(response: string, deviceId: number): RouterOsScriptResult {
    const text = response || '';
    const parsed = this.tryParseJson(text);
    const payload = parsed?.data || parsed?.result || parsed;

    if (payload && typeof payload === 'object') {
      return {
        deviceID: this.optionalId(payload.deviceID ?? payload.deviceId) ?? deviceId,
        vehicleID: this.optionalId(payload.vehicleID ?? payload.vehicleId),
        deviceSIMID: this.optionalId(payload.deviceSIMID ?? payload.deviceSimID ?? payload.deviceSIMId ?? payload.deviceSimId),
        primarySIMID: this.optionalId(payload.primarySIMID ?? payload.primarySimID ?? payload.primarySIMId ?? payload.primarySimId ?? payload.deviceSIMID ?? payload.deviceSimID),
        secondarySIMID: this.optionalId(payload.secondarySIMID ?? payload.secondarySimID ?? payload.secondarySIMId ?? payload.secondarySimId),
        vehicleRegistration: payload.vehicleRegistration,
        deviceSerialNumber: payload.deviceSerialNumber,
        routerOsScript: payload.routerOsScript || payload.routerOSScript || payload.script || text,
        publicDeparturesUrl: payload.publicDeparturesUrl || payload.PublicDeparturesUrl
      };
    }

    return {
      deviceID: deviceId,
      routerOsScript: text
    };
  }

  private tryParseJson(text: string): any | null {
    const trimmed = text.trim();

    if (!trimmed || (!trimmed.startsWith('{') && !trimmed.startsWith('['))) {
      return null;
    }

    try {
      return JSON.parse(trimmed);
    } catch {
      return null;
    }
  }

  private optionalId(value: any): number | undefined {
    const numeric = Number(value);
    return value !== null && value !== undefined && value !== '' && Number.isFinite(numeric) && numeric > 0
      ? numeric
      : undefined;
  }
}
